import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FormProvider } from 'react-hook-form';
import { Row, Col, Switch, Alert, Progress, Spin } from 'antd';
import { listarHomologacoesPorCliente, buscarLimiteHomologacoes } from '../api/homologacao';

import { Controller } from 'react-hook-form';
import { DeleteOutlined, CheckCircleOutlined, ExclamationCircleOutlined, CloudUploadOutlined, EyeOutlined, WarningOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import FormField from '../components/shared/FormField';
import SelectField from '../components/shared/SelectField';
import MaskedInput from '../components/shared/MaskedInput';
import UltimatePicker from '../components/UltimatePicker';
import { CPF_MASK, CNPJ_MASK, CEP_MASK, PHONE_MASK } from '../utils/masks';
import { useCepLookup } from '../hooks/useCepLookup';
import { useFormStore } from '../store/formStore';
import { useHomologacao } from '../hooks/useHomologacao';
import { obterUrlArquivoHomologacao } from '../api/homologacao';
import { buscarPedidosCalc, type CalcPedido } from '../api/crm';

// Empresas cuja proposta segue para homologação mesmo sem estoque (só com aviso).
const EMPRESAS_SEGUEM_SEM_ESTOQUE = ['MTZ', 'PME'];
import { extrairDadosPedido, extrairPotenciaUnitariaInversorKW, filtrarPropostasValidas } from '../utils/proposta';

/* ─── option lists ─── */
const TIPO_LOCALIZACAO_OPTIONS = [
  { label: 'Urbana', value: 'urbana' },
  { label: 'Rural', value: 'rural' },
];
const TIPO_ESTRUTURA_INSTALACAO_OPTIONS = [
  { label: 'Metálico', value: 'Metalico' },
  { label: 'Cerâmico', value: 'Ceramico' },
  { label: 'Solo', value: 'Solo' },
  { label: 'Carport', value: 'Carport' },
  { label: 'Fibrocimento', value: 'Fibrocimento' },
  { label: 'Fibromadeira', value: 'Fibromadeira' },
  { label: 'Laje', value: 'Laje' },
];
const TIPO_PADRAO_OPTIONS = [
  { label: 'Individual', value: 'Individual' },
  { label: 'Coletivo', value: 'Coletivo' },
];
const TIPO_ESTRUTURA_PADRAO_OPTIONS = [
  { label: 'Pontalete', value: 'Pontalete' },
  { label: 'Alvenaria', value: 'Alvenaria' },
];
const FASES_OPTIONS = [
  { label: 'Monofásico', value: 'Monofasico' },
  { label: 'Bifásico', value: 'Bifasico' },
  { label: 'Trifásico', value: 'Trifasico' },
];
const SIM_NAO_OPTIONS = [
  { label: 'Sim', value: 'Sim' },
  { label: 'Não', value: 'Nao' },
];
const TIPO_ALTERACAO_CARGA_OPTIONS = [
  { label: 'Ligação Nova', value: 'Ligação Nova' },
  { label: 'Alteração pelo cliente (padronista)', value: 'Alteração pelo cliente' },
  { label: 'Alteração pela Multiluz junto com GD', value: 'Alteração pela Multiluz' },
];
const TIPO_TITULAR_OPTIONS = [
  { label: 'Pessoa Física (PF)', value: 'PF' },
  { label: 'Pessoa Jurídica (PJ)', value: 'PJ' },
];
const FAVOR_CONTRA_REDE_OPTIONS = [
  { label: 'A favor da rede', value: 'a favor da rede' },
  { label: 'Contra a rede', value: 'contra a rede' },
];
const SERVICO_ALVENARIA_OPTIONS = [
  { label: 'Sem serviço de alvenaria', value: 'sem serviço de alvenaria' },
  { label: 'Com serviço de alvenaria', value: 'com serviço de alvenaria' },
];
const SERVICO_ADICIONAL_DETALHADO_OPTIONS = [
  { label: 'Não', value: 'Nao' },
  { label: 'Padrão VENDIDO e INSTALADO pela Multiluz', value: 'Padrão VENDIDO e INSTALADO pela Multiluz' },
  { label: 'Padrão VENDIDO pela Multiluz e instalado por TERCEIROS', value: 'Padrão VENDIDO pela Multiluz e instalado por TERCEIROS' },
  { label: 'Outros Serviços', value: 'Outros Serviços' },
  { label: 'Padrão VENDIDO e INSTALADO pela Multiluz + Outros Serviços', value: 'Padrão VENDIDO e INSTALADO pela Multiluz + Outros Serviços' },
  { label: 'Padrão executado por TERCEIROS', value: 'Padrão executado por TERCEIROS' },
];

/* Options alinhadas com ContratoWizard (usadas quando homologacao_previa = Sim) */
const TIPO_CLIENTE_OPTIONS = [
  { label: 'Pessoa Física', value: 'Pessoa Física' },
  { label: 'Pessoa Jurídica', value: 'Pessoa Jurídica' },
];
const ESTADO_CIVIL_OPTIONS = [
  'Solteiro(a)', 'Casado(a)', 'Divorciado(a)', 'Viúvo(a)', 'União Estável',
].map((v) => ({ label: v, value: v }));
const MODALIDADE_COMPENSACAO_OPTIONS = [
  'Autoconsumo Remoto com possibilidade de local (FAST TRACK)',
  'Autoconsumo Remoto',
  'Autoconsumo Local (FAST TRACK)',
  'EMUC',
  'Geração Distribuída',
].map((v) => ({ label: v, value: v }));
const LOCAL_INSTALACAO_OPTIONS = [
  { label: 'Urbano', value: 'Urbano' },
  { label: 'Rural', value: 'Rural' },
];
const SITUACAO_PADRAO_OPTIONS = [
  { label: 'Sem alteração', value: 'Sem alteração' },
  { label: 'Ligação Nova', value: 'Ligação Nova' },
  { label: 'Alteração de carga', value: 'Alteração de carga' },
];
const TIPO_CONEXAO_PADRAO_OPTIONS = [
  { label: 'Monofásico', value: 'Monofásico' },
  { label: 'Bifásico', value: 'Bifásico' },
  { label: 'Trifásico', value: 'Trifásico' },
];
const CORRENTE_DISJUNTOR_OPTIONS = [
  '40A', '50A', '63A', '70A', '80A', '100A', '125A', '150A',
].map((v) => ({ label: v, value: v }));
const FAVOR_OU_CONTRA_REDE_OPTIONS = [
  { label: 'Favor da rede', value: 'Favor da rede' },
  { label: 'Contra a rede', value: 'Contra a rede' },
];
const SERVICO_ALVENARIA_WIZ_OPTIONS = [
  { label: 'Sim', value: 'Sim' },
  { label: 'Não', value: 'Não' },
];
const TIPO_ESTRUTURA_WIZ_OPTIONS = [
  'Metálico', 'Cerâmico', 'para Solo', 'Carport', 'Fibrocimento', 'Fibromadeira', 'Laje',
].map((v) => ({ label: v, value: v }));
const UF_OPTIONS = [
  'AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT',
  'PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO',
].map((uf) => ({ label: uf, value: uf }));

const HOMOLOG_PREVIA_STEP_KEYS = ['tipo', 'cliente', 'titular', 'endereco'] as const;
type StepKey = 'tipo' | 'cliente' | 'titular' | 'endereco' | 'projeto' | 'padrao' | 'servicos' | 'fotos' | 'finalizar';

interface StepDef { key: StepKey; title: string; }

function buildActiveSteps(homologPrevia: boolean): StepDef[] {
  const steps: StepDef[] = [{ key: 'tipo', title: 'Tipo' }];
  if (homologPrevia) {
    steps.push(
      { key: 'cliente', title: 'Cliente' },
      { key: 'titular', title: 'Titular' },
      { key: 'endereco', title: 'Endereço' },
    );
  }
  steps.push(
    { key: 'projeto', title: 'Projeto' },
    { key: 'padrao', title: 'Padrão Atual' },
    { key: 'servicos', title: 'Serviços Adicionais' },
    { key: 'fotos', title: 'Fotos Técnicas' },
    { key: 'finalizar', title: 'Finalizar' },
  );
  return steps;
}

const DEFAULTS = {
  /* Tipo */
  homologacao_previa: undefined as boolean | undefined,

  /* Projeto (existentes) */
  ampliacao_usina: false, potencia_existente_kw: '', descricao_usina_existente: '',
  potencia_adquirida_kwp: '', numero_modulos: '', concessionaria: '', tipo_localizacao: '', tipo_estrutura_instalacao: '',
  coordenadas_transformador: '',
  /* Local da Instalação (Leaflet + Street View) */
  coordenada_geografica: '', url_local_instalacao: '',
  link_street_view: '', thumbnail_street_view: '',
  tipo_padrao: '', tipo_estrutura_padrao: '',
  tem_disjuntor_geral: '', corrente_disjuntor_geral: '',
  tipo_titular: '', todas_contas_mesmo_titular: '',
  corrente_padrao: '', fases_disjuntor: '', descricao_padrao: '',
  tem_alteracao_carga: '', tipo_alteracao_carga: '', descricao_alteracao: '', coord_padrao_novo: '',
  alteracao_coletivo: '', descricao_alteracao_coletivo: '',
  servico_adicional: '', descricao_servico_adicional: '',
  favor_contra_rede: '', servico_alvenaria: '',
  projeto_aprovado: '', nome_parecer_aprovado: '',
  observacoes_fotos: '', observacoes_finais: '',

  /* Campos ContratoWizard (quando homologacao_previa = true) — usamos os mesmos
     nomes do initialContrato pra o merge no prefill funcionar direto. */
  // Cliente
  tipoCliente: 'Pessoa Física',
  nomeResponsavel: '', email: '', telefone: '', cpfResponsavel: '',
  estadoCivil: '', profissao: '',
  razaoSocial: '', cnpjEmpresa: '', finalidadeEmpresa: '', cargoResponsavel: '',
  // Titular
  titularIgualCliente: true,
  titularTipo: 'Pessoa Física',
  titularNome: '', titularCpf: '', titularEmail: '', titularTelefone: '',
  titularEstadoCivil: '', titularProfissao: '',
  titularRazaoSocial: '', titularCnpj: '',
  titularCargoResponsavel: '', titularFinalidadeEmpresa: '',
  // Endereço Faturamento
  cepFaturamento: '', logradouroFaturamento: '', numeroFaturamento: '',
  bairroFaturamento: '', cidadeFaturamento: '', ufFaturamento: '', complementoFaturamento: '',
  // Endereço Instalação
  instalacaoIgualContratual: true,
  cepInstalacao: '', logradouroInstalacao: '', numeroInstalacao: '',
  bairroInstalacao: '', cidadeInstalacao: '', ufInstalacao: '', complementoInstalacao: '',
  // Projeto extra
  idProposta: '', numeroProposta: '',
  valorProposta: 0,
  modeloModulo: '', marcaInversores: '', modeloInversor1: '',
  qtdInversores: 0, qtdInversor1: 0, potenciaTotalInversores: 0,
  modeloInversor2: '', qtdInversor2: 0, modeloInversor3: '', qtdInversor3: 0,
  inversores: [] as { modelo: string; quantidade: number }[],
  modalidadeCompensacao: '', localInstalacao: '', tipoEstrutura: '', contratoComSeguro: false,
  // Serviços extra
  situacaoPadrao: '', quantidadePadroes: 1,
  tipoConexaoPadrao: '', correnteDisjuntor: '',
  favorOuContraRede: '', servicoAlvenariaWiz: '',
  valorPadrao: 0, aditivos: '',
};

/* Required fields por step-key (usado pelo isStepValid). */
const STEP_REQUIRED: Record<StepKey, string[]> = {
  tipo: ['homologacao_previa'],
  cliente: [], // validado via função (depende de tipoCliente PF/PJ)
  titular: [], // validado via função (depende de titularIgualCliente)
  endereco: [], // validado via função (depende de instalacaoIgualContratual)
  projeto: ['potencia_adquirida_kwp', 'concessionaria', 'tipo_localizacao'],
  padrao: ['tipo_padrao', 'todas_contas_mesmo_titular', 'corrente_padrao', 'fases_disjuntor'],
  servicos: ['servico_adicional'],
  fotos: [],
  finalizar: [],
};

const REQUIRED_UPLOADS = ['foto_fachada', 'print_marcacao_telhado'];

/* ─── Thumbnail ─── */
function FileThumbnail({ item, onDelete }: {
  item: { id: number; nome_original: string; mime_type?: string };
  onDelete: (id: number) => void;
}) {
  const isImage = item.mime_type?.startsWith('image/');
  const streamUrl = obterUrlArquivoHomologacao(item.id);

  const handleView = () => {
    window.open(streamUrl, '_blank');
  };

  const isPdf = item.mime_type === 'application/pdf';

  return (
    <div className="relative group border border-neutral-200 rounded-md overflow-hidden bg-white" style={{ width: 80, height: 80 }}>
      {isImage ? (
        <img src={streamUrl} alt={item.nome_original} className="w-full h-full object-cover" />
      ) : isPdf ? (
        <div className="w-full h-full relative bg-neutral-50">
          <iframe src={`${streamUrl}#toolbar=0&navpanes=0&scrollbar=0`} title={item.nome_original}
            className="w-full h-full pointer-events-none" style={{ border: 'none', transform: 'scale(1)', transformOrigin: 'top left' }} />
          <div className="absolute bottom-0 left-0 right-0 bg-white/80 px-1 py-0.5">
            <span className="text-[8px] text-neutral-500 truncate block">{item.nome_original}</span>
          </div>
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center bg-neutral-50 p-1">
          <span className="text-xs font-mono text-neutral-400 uppercase">arquivo</span>
          <span className="text-[9px] text-neutral-400 truncate w-full text-center mt-1">{item.nome_original}</span>
        </div>
      )}
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
        <button type="button" onClick={handleView} className="text-white hover:text-neutral-300"><EyeOutlined /></button>
        <button type="button" onClick={() => onDelete(item.id)} className="text-white hover:text-neutral-300"><DeleteOutlined /></button>
      </div>
    </div>
  );
}

/* ─── Upload ─── */
function UploadField({ label, categoria, required, upload, deletar, arquivos, uploadStates }: {
  label: string; categoria: string; required?: boolean;
  upload: (cat: string, file: File) => Promise<number | null>;
  deletar: (id: number) => Promise<boolean>;
  arquivos: { id: number; nome_original: string; mime_type?: string; categoria: string }[];
  uploadStates: Record<string, { uploading: boolean; progress: number }>;
}) {
  const filtered = arquivos.filter((a) => a.categoria === categoria);
  const state = uploadStates[categoria];
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [localPreviews, setLocalPreviews] = useState<{ name: string; url: string; isImage: boolean }[]>([]);

  const processFiles = (files: FileList | null) => {
    if (!files) return;
    const newPreviews: typeof localPreviews = [];
    for (const file of Array.from(files)) {
      const isImage = file.type.startsWith('image/');
      newPreviews.push({ name: file.name, url: isImage ? URL.createObjectURL(file) : '', isImage });
      upload(categoria, file).then(() => {
        setLocalPreviews((prev) => prev.filter((p) => p.name !== file.name));
      });
    }
    setLocalPreviews((prev) => [...prev, ...newPreviews]);
  };

  useEffect(() => {
    return () => { localPreviews.forEach((p) => { if (p.url) URL.revokeObjectURL(p.url); }); };
  }, []);

  return (
    <div className="mb-5">
      <label className="block text-sm font-medium text-neutral-600 mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
        {filtered.length > 0 && <span className="ml-2 text-neutral-400">{filtered.length} arquivo(s)</span>}
      </label>
      <input ref={inputRef} type="file" className="hidden" multiple accept="image/*,.pdf"
        onChange={(e) => { processFiles(e.target.files); e.target.value = ''; }} />
      <div
        className={`flex h-20 w-full cursor-pointer flex-col items-center justify-center rounded-md border border-dashed transition-colors duration-200 ${dragOver ? 'border-black bg-neutral-50' : 'border-neutral-300 bg-white hover:bg-neutral-50'}`}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); processFiles(e.dataTransfer.files); }}
      >
        {state?.uploading ? (
          <div className="text-center w-3/4">
            <p className="text-xs text-neutral-500 animate-pulse mb-1">Enviando...</p>
            <Progress percent={state.progress} size="small" strokeColor="#000" />
          </div>
        ) : (
          <>
            <CloudUploadOutlined className="text-lg text-neutral-400 mb-1" />
            <p className="text-[11px] text-neutral-400">Clique ou arraste arquivos</p>
          </>
        )}
      </div>
      {localPreviews.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {localPreviews.map((p) => (
            <div key={p.name} className="border border-neutral-200 rounded-md overflow-hidden bg-neutral-50 animate-pulse" style={{ width: 80, height: 80 }}>
              {p.isImage && p.url
                ? <img src={p.url} alt={p.name} className="w-full h-full object-cover opacity-60" />
                : <div className="w-full h-full flex items-center justify-center text-[10px] text-neutral-400">{p.name}</div>}
            </div>
          ))}
        </div>
      )}
      {filtered.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {filtered.map((item) => <FileThumbnail key={item.id} item={item} onDelete={deletar} />)}
        </div>
      )}
    </div>
  );
}

export default function HomologacaoPage() {
  const { id: paramId, clienteId: paramClienteId } = useParams<{ id: string; clienteId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const existingId = paramId ? Number(paramId) : undefined;
  const clienteId = paramClienteId ? Number(paramClienteId) : undefined;
  const isNovoComCliente = !existingId && !!clienteId;
  const [checkingExistentes, setCheckingExistentes] = useState(isNovoComCliente);

  useEffect(() => {
    if (!isNovoComCliente) return;
    Promise.all([
      listarHomologacoesPorCliente(clienteId!),
      buscarLimiteHomologacoes(clienteId!),
    ])
      .then(([lista, limite]) => {
        const ativasCount = lista.filter((h) => h.ativa !== 0).length;
        if (ativasCount >= limite) navigate(`/homologacoes/${clienteId}`, { replace: true });
      })
      .catch(() => {})
      .finally(() => setCheckingExistentes(false));
  }, []);

  // Valores pré-preenchidos via URL
  // ex: ?potencia=10.5&estrutura=Metalico&tipo_pessoa=PJ&nome=João Silva&cpf_cnpj=12.345.678/0001-90&IdCliente=12345
  const urlPotencia    = searchParams.get('potencia')    ?? '';
  const urlEstrutura   = searchParams.get('estrutura')   ?? '';
  const urlTipoPessoa  = searchParams.get('tipo_pessoa') ?? ''; // PF | PJ
  const urlNome        = searchParams.get('nome')        ?? '';
  const urlCpfCnpj     = searchParams.get('cpf_cnpj')   ?? '';
  const urlIdCliente   = searchParams.get('IdCliente') ?? searchParams.get('idCliente') ?? '';
  // O cliente_id da rota/homologação JÁ É o IdCliente do Calc (mesmo formato 10 dígitos)
  // então usamos como fallback quando não vier explicitamente na query string.
  const fromUrl        = !existingId && (urlNome || urlTipoPessoa || urlPotencia);

  const {
    form, homologacaoId, arquivos, submitting, uploadStates,
    salvar, finalizar, upload, deletar, resolvedClienteId,
  } = useHomologacao(existingId, DEFAULTS, clienteId);
  const { watch } = form;

  useEffect(() => {
    if (homologacaoId && !paramId) {
      navigate(`/homologacao/${homologacaoId}`, { replace: true });
    }
  }, [homologacaoId, paramId, navigate]);

  // Pré-preencher campos da URL (só uma vez, em nova homologação)
  useEffect(() => {
    if (!existingId) {
      if (urlPotencia)   form.setValue('potencia_adquirida_kwp',  urlPotencia);
      if (urlEstrutura)  form.setValue('tipo_estrutura_instalacao', urlEstrutura);
      if (urlTipoPessoa) form.setValue('tipo_titular', urlTipoPessoa);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Prefill do card Pipefy via apipipefy (só em novas homologações e só em campos vazios)
  const prefillApipipefyRef = useRef(false);
  useEffect(() => {
    if (existingId || !clienteId || prefillApipipefyRef.current) return;
    prefillApipipefyRef.current = true;
    const apipipefyUrl =
      (import.meta as any).env.VITE_APIPIPEFY_URL || 'https://apipipefy.grupomultiluz.com.br';
    fetch(`${apipipefyUrl}/api/contrato-homologacao-prefill/${clienteId}?source=homologacao`)
      .then((r) => r.json())
      .then((d) => {
        if (!d?.success || d.alreadyExists) return;
        // `0` conta como vazio: os defaults numéricos do formulário (valorProposta,
        // qtdInversores, potenciaTotalInversores) bloqueavam o prefill.
        const vazio = (v: any) => v == null || v === '' || v === 0;
        const setIfEmpty = (field: any, value: any) => {
          if (value == null || value === '') return;
          if (vazio(form.getValues(field))) form.setValue(field, value);
        };
        // tipoCliente/titularTipo têm default 'Pessoa Física' — nunca ficam vazios, então
        // setIfEmpty jamais os sobrescrevia e todo cliente PJ entrava como PF.
        if (d.contrato?.tipoCliente) {
          form.setValue('tipoCliente', d.contrato.tipoCliente);
          form.setValue('titularTipo', d.contrato.tipoCliente);
        }
        // Campos nativos da homologação (snake_case)
        if (d.homologacao) {
          const h = d.homologacao;
          setIfEmpty('potencia_adquirida_kwp', h.potencia_adquirida_kwp);
          setIfEmpty('numero_modulos', h.numero_modulos);
          setIfEmpty('tipo_estrutura_instalacao', h.tipo_estrutura_instalacao);
          setIfEmpty('fases_disjuntor', h.fases_disjuntor);
          setIfEmpty('tipo_titular', h.tipo_titular);
          setIfEmpty('tipo_localizacao', h.tipo_localizacao);
          setIfEmpty('observacoes_finais', h.observacoes_finais); // "Notas" do funil comercial → Observações Gerais
        }
        // Campos do ContratoWizard (camelCase) — preenchem os steps Cliente/Titular/Endereço/Projeto
        // mesmo que o usuário ainda não tenha marcado homologação prévia, pra serem reaproveitados
        // imediatamente caso ele marque Sim.
        if (d.contrato) {
          const c = d.contrato;
          // Cliente
          setIfEmpty('tipoCliente', c.tipoCliente);
          setIfEmpty('nomeResponsavel', c.nomeResponsavel);
          setIfEmpty('email', c.email);
          setIfEmpty('telefone', c.telefone);
          setIfEmpty('cpfResponsavel', c.cpfResponsavel);
          setIfEmpty('estadoCivil', c.estadoCivil);
          setIfEmpty('profissao', c.profissao);
          setIfEmpty('razaoSocial', c.razaoSocial);
          setIfEmpty('cnpjEmpresa', c.cnpjEmpresa);
          setIfEmpty('finalidadeEmpresa', c.finalidadeEmpresa);
          setIfEmpty('cargoResponsavel', c.cargoResponsavel);
          // Endereço faturamento
          setIfEmpty('cepFaturamento', c.cepFaturamento);
          setIfEmpty('logradouroFaturamento', c.logradouroFaturamento);
          setIfEmpty('numeroFaturamento', c.numeroFaturamento);
          setIfEmpty('bairroFaturamento', c.bairroFaturamento);
          setIfEmpty('cidadeFaturamento', c.cidadeFaturamento);
          setIfEmpty('ufFaturamento', c.ufFaturamento);
          setIfEmpty('complementoFaturamento', c.complementoFaturamento);
          // Endereço instalação
          setIfEmpty('cepInstalacao', c.cepInstalacao);
          setIfEmpty('logradouroInstalacao', c.logradouroInstalacao);
          setIfEmpty('numeroInstalacao', c.numeroInstalacao);
          setIfEmpty('bairroInstalacao', c.bairroInstalacao);
          setIfEmpty('cidadeInstalacao', c.cidadeInstalacao);
          setIfEmpty('ufInstalacao', c.ufInstalacao);
          setIfEmpty('complementoInstalacao', c.complementoInstalacao);
          // Projeto (calc)
          setIfEmpty('valorProposta', c.valorProposta);
          setIfEmpty('potenciaTotalInversores', c.potenciaTotalInversores);
          setIfEmpty('modeloModulo', c.modeloModulo);
          setIfEmpty('marcaInversores', c.marcaInversores);
          setIfEmpty('modeloInversor1', c.modeloInversor1);
          setIfEmpty('qtdInversores', c.qtdInversores);
          setIfEmpty('qtdInversor1', c.qtdInversor1);
          setIfEmpty('tipoEstrutura', c.tipoEstrutura);
          setIfEmpty('modalidadeCompensacao', c.modalidadeCompensacao);
          setIfEmpty('localInstalacao', c.localInstalacao);
          // qtdModulos/potenciaProjeto do calc preenchem também os campos snake-case da homologação
          if (c.qtdModulos != null) setIfEmpty('numero_modulos', c.qtdModulos);
          if (c.potenciaProjeto != null) setIfEmpty('potencia_adquirida_kwp', c.potenciaProjeto);
        }
      })
      .catch((err) => console.warn('[prefill homologacao]', err?.message ?? err));
  }, [existingId, clienteId, form]);

  const loadCatalogos = useFormStore((s) => s.loadCatalogos);
  const concessionarias = useFormStore((s) => s.concessionarias);
  useEffect(() => { loadCatalogos(); }, [loadCatalogos]);

  // ── Proposta (Calc) ──────────────────────────────────────────────
  const [propostas, setPropostas] = useState<CalcPedido[]>([]);
  const [propostaSelecionada, setPropostaSelecionada] = useState<CalcPedido | null>(null);
  const [loadingProposta, setLoadingProposta] = useState(false);
  const [propostaRefresh, setPropostaRefresh] = useState(0);

  // Falta de estoque só BARRA a aplicação da proposta para empresas que não
  // sejam MTZ/PME. Para elas, a proposta é aplicada normalmente e apenas exibe a
  // flag de aviso (ver Alert abaixo) — o setor responsável é comunicado à parte.
  // PME = pipe "MULTILUZ SOLAR - COMERCIAL B2C - PME", que usava a MTZ até 03/11/2026.
  const bloqueadoPorEstoque = useCallback((p: CalcPedido | null | undefined) => {
    if (!p?.estoqueIndisponivel || !p.estoqueIndisponivel.length) return false;
    return !EMPRESAS_SEGUEM_SEM_ESTOQUE.includes(String(p.Empresa || '').trim().toUpperCase());
  }, []);

  const aplicarProposta = useCallback((pedido: CalcPedido) => {
    const d = extrairDadosPedido(pedido);
    // Sempre sobrescreve (sem if), pra recarregar substituir dados antigos
    // Vínculo com a proposta (obrigatório p/ finalizar prévia — ver guard no Finalizar)
    form.setValue('idProposta', d.idProposta || '');
    form.setValue('numeroProposta', d.numeroProposta || '');
    form.setValue('valorProposta', d.valorProposta || 0);
    form.setValue('modeloModulo', d.modeloModulo || '');
    form.setValue('marcaInversores', d.marcaInversores || '');
    form.setValue('modeloInversor1', d.modeloInversor1 || '');
    form.setValue('qtdInversores', d.qtdInversores || 0);
    form.setValue('qtdInversor1', d.qtdInversor1 || 0);
    form.setValue('modeloInversor2', d.modeloInversor2 || '');
    form.setValue('qtdInversor2', d.qtdInversor2 || 0);
    form.setValue('modeloInversor3', d.modeloInversor3 || '');
    form.setValue('qtdInversor3', d.qtdInversor3 || 0);
    form.setValue('inversores' as any, d.inversores as any);
    form.setValue('potenciaTotalInversores', d.potenciaTotalInversores || 0);
    // Campos snake_case da homologação (também usados em VendaConcluida)
    form.setValue('numero_modulos', (d.qtdModulos || '') as any);
    if (d.potenciaProjeto) form.setValue('potencia_adquirida_kwp', d.potenciaProjeto as any);
    // Tipo de Estrutura derivado do TipoTelhado da proposta (só sobrescreve se mapeou).
    if (d.tipoEstruturaInstalacao) form.setValue('tipo_estrutura_instalacao', d.tipoEstruturaInstalacao);
  }, [form]);

  // IdCliente efetivo: query param > clienteId da rota > resolvedClienteId (homologação carregada)
  const effectiveIdCliente = useMemo(() => {
    if (urlIdCliente) return urlIdCliente;
    if (clienteId) return String(clienteId);
    if (resolvedClienteId) return String(resolvedClienteId);
    return '';
  }, [urlIdCliente, clienteId, resolvedClienteId]);

  useEffect(() => {
    if (!effectiveIdCliente) return;
    // Homologação existente: espera os dados gravados carregarem antes de decidir o vínculo.
    if (existingId && !homologacaoId) return;
    // Vínculo já gravado só muda por ação explícita ("Recarregar proposta" ou o seletor).
    // Abrir pra editar não pode trocar/soltar a proposta sozinho: isso muda o conteúdo e o
    // backend invalida a procuração já assinada (caso #1945, 14/10/2026).
    const idSalvo = existingId ? String(form.getValues('idProposta') || '') : '';
    const automatico = propostaRefresh === 0;
    setLoadingProposta(true);
    setPropostaSelecionada(null);
    buscarPedidosCalc(effectiveIdCliente)
      .then((pedidos) => {
        const validas = filtrarPropostasValidas(pedidos);
        setPropostas(validas);
        if (automatico && idSalvo) {
          setPropostaSelecionada(pedidos.find((p) => p.IdPedidoBanco === idSalvo) || null);
          return;
        }
        const primeiraOk = validas.find((p) => !bloqueadoPorEstoque(p));
        if (primeiraOk) {
          setPropostaSelecionada(primeiraOk);
          aplicarProposta(primeiraOk);
        } else if (idSalvo) {
          toast.warning(`Nenhuma proposta válida no Calc. Mantido o vínculo com a proposta nº ${form.getValues('numeroProposta') || idSalvo}.`);
        } else {
          // Nenhuma proposta aplicável (todas vencidas ou bloqueadas por estoque):
          // solta o vínculo pra que a finalização fique bloqueada (regra B).
          form.setValue('idProposta', '');
          form.setValue('numeroProposta', '');
          if (validas.length) setPropostaSelecionada(validas[0]);
        }
      })
      .catch((err) => console.warn('[proposta calc]', err?.message ?? err))
      .finally(() => setLoadingProposta(false));
  }, [effectiveIdCliente, propostaRefresh, aplicarProposta, bloqueadoPorEstoque, existingId, homologacaoId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [step, setStep] = useState(0);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const { buscarCep } = useCepLookup();

  // homologacao_previa dirige a estrutura dos steps; watched para reatividade
  const homologacaoPrevia = watch('homologacao_previa') === true;
  const inversoresProposta = ((watch('inversores' as any) as unknown) as { modelo: string; quantidade: number }[] | undefined) || [];
  const activeSteps = useMemo(() => buildActiveSteps(homologacaoPrevia), [homologacaoPrevia]);
  const currentStepKey: StepKey | undefined = activeSteps[step]?.key;

  // Garante que o step atual é válido quando activeSteps encolhe (troca Sim → Não)
  useEffect(() => {
    if (step >= activeSteps.length) setStep(activeSteps.length - 1);
  }, [activeSteps.length, step]);

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const subscription = form.watch(() => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveTimerRef.current = setTimeout(async () => {
        const data = form.getValues();
        await salvar(data, true);
        setLastSaved(new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
      }, 1500);
    });
    return () => { subscription.unsubscribe(); if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [form, salvar]);

  const ampliacao = watch('ampliacao_usina');
  const localizacao = watch('tipo_localizacao');
  const tipoEstrutura = watch('tipo_estrutura_instalacao');
  const potencia = watch('potencia_adquirida_kwp');
  const numeroModulos = watch('numero_modulos');
  const tipoPadrao = watch('tipo_padrao');
  const temDisjuntorGeral = watch('tem_disjuntor_geral');
  const tipoTitular = watch('tipo_titular');
  const todasContasTitular = watch('todas_contas_mesmo_titular');
  const temAlteracaoCarga = watch('tem_alteracao_carga');
  const tipoAlteracaoCarga = watch('tipo_alteracao_carga');
  const alteracaoColetivo = watch('alteracao_coletivo');
  const servicoAdicional = watch('servico_adicional');
  // Campos da homologação prévia
  const tipoClienteWiz = watch('tipoCliente');
  const titularIgualCliente = watch('titularIgualCliente');
  const titularTipoWiz = watch('titularTipo');
  const instalacaoIgualContratual = watch('instalacaoIgualContratual');
  const situacaoPadraoWiz = watch('situacaoPadrao');

  const concOptions = concessionarias.map((c) => ({ label: c.nome, value: c.id }));
  const uploadProps = { upload, deletar, arquivos, uploadStates };

  // ── Condicionais ──
  const isColetivo = tipoPadrao === 'Coletivo';
  const isRural = localizacao === 'rural';
  const isAlteracaoCarga = temAlteracaoCarga === 'Sim';
  const isLigacaoNova = tipoAlteracaoCarga === 'Ligação Nova';
  const isAlteracaoCliente = tipoAlteracaoCarga === 'Alteração pelo cliente';
  const isPJ = tipoTitular === 'PJ';

  // Doc imóvel: rural→step0, coletivo→sempre no step1, individual+alteração+urbano→na alteração
  const needsDocImovelColetivo = isColetivo && !isRural;
  const needsDocImovelAlteracao = isAlteracaoCarga && !isRural && !isColetivo;
  const needsIptu = isLigacaoNova && !isRural;

  // Alerta projeto separado: acima de 20 módulos ou solo/laje
  const needsProjetoSeparado = Number(numeroModulos) > 20 || tipoEstrutura === 'Solo' || tipoEstrutura === 'Laje';

  // ── Validação por step ──
  const isStepValid = useCallback((s: number): boolean => {
    const values = form.getValues() as any;
    const stepDef = activeSteps[s];
    if (!stepDef) return false;
    const key = stepDef.key;
    for (const f of STEP_REQUIRED[key] || []) {
      const val = values[f];
      if (val === undefined || val === null || val === '') return false;
    }
    if (key === 'tipo') return values.homologacao_previa === true || values.homologacao_previa === false;
    if (key === 'cliente') {
      const base = !!(values.nomeResponsavel && values.cpfResponsavel && values.telefone && values.email && values.estadoCivil && values.profissao);
      if (!base) return false;
      if (values.tipoCliente === 'Pessoa Jurídica') {
        if (!(values.razaoSocial && values.cnpjEmpresa)) return false;
        if (!arquivos.some((a) => a.categoria === 'cartao_cnpj')) return false;
        if (!arquivos.some((a) => a.categoria === 'contrato_social')) return false;
      }
      if (!arquivos.some((a) => a.categoria === 'doc_identidade_cliente')) return false;
      if (!arquivos.some((a) => a.categoria === 'comprovante_endereco_cliente')) return false;
      return true;
    }
    if (key === 'titular') {
      if (values.titularIgualCliente !== false) return true; // true ou undefined = igual cliente
      const baseOk = values.titularTipo === 'Pessoa Jurídica'
        ? !!(values.titularRazaoSocial && values.titularCnpj && values.titularNome && values.titularEmail)
        : !!(values.titularNome && values.titularCpf && values.titularEmail && values.titularTelefone);
      if (!baseOk) return false;
      if (!arquivos.some((a) => a.categoria === 'doc_identidade_titular')) return false;
      if (values.titularTipo === 'Pessoa Jurídica' && !arquivos.some((a) => a.categoria === 'contrato_social_titular')) return false;
      return true;
    }
    if (key === 'endereco') {
      const baseFat = !!(values.cepFaturamento && values.logradouroFaturamento && values.numeroFaturamento && values.cidadeFaturamento && values.ufFaturamento && values.bairroFaturamento);
      if (!baseFat) return false;
      if (values.instalacaoIgualContratual !== false) return true;
      return !!(values.cepInstalacao && values.logradouroInstalacao && values.numeroInstalacao && values.cidadeInstalacao && values.ufInstalacao && values.bairroInstalacao);
    }
    if (key === 'fotos') {
      return REQUIRED_UPLOADS.every((cat) => arquivos.some((a) => a.categoria === cat));
    }
    return true;
  }, [form, arquivos, activeSteps]);

  const stepStatus = (s: number): 'finish' | 'process' | 'error' | 'wait' => {
    if (s === step) return 'process';
    if (s < step) return isStepValid(s) ? 'finish' : 'error';
    return 'wait';
  };

  const autoSave = useCallback(async () => {
    const data = form.getValues();
    await salvar(data, true);
    setLastSaved(new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }));
  }, [form, salvar]);

  const handleNext = useCallback(async () => {
    if (!isStepValid(step)) {
      toast.warning('Preencha os campos obrigatórios antes de continuar');
      return;
    }
    await autoSave();
    setStep((s) => Math.min(s + 1, activeSteps.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [step, isStepValid, autoSave, activeSteps.length]);

  const handlePrev = useCallback(() => {
    setStep((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // CEP → busca endereço (faturamento ou instalação)
  const handleCepBlur = useCallback(async (prefix: 'Faturamento' | 'Instalacao') => {
    const cep = form.getValues(`cep${prefix}` as any) as string | undefined;
    if (!cep) return;
    const result = await buscarCep(cep);
    if (!result) return;
    form.setValue(`logradouro${prefix}` as any, result.logradouro);
    form.setValue(`bairro${prefix}` as any, result.bairro);
    form.setValue(`cidade${prefix}` as any, result.cidade);
    form.setValue(`uf${prefix}` as any, result.uf);
  }, [form, buscarCep]);

  const handleSave = useCallback(async () => {
    await autoSave();
    toast.success('Homologação salva com sucesso!');
  }, [autoSave]);

  // ── Checklist ──
  const checklistItems = [
    { label: 'Potência Adquirida', ok: !!watch('potencia_adquirida_kwp') },
    { label: 'Concessionária', ok: !!watch('concessionaria') },
    { label: 'Tipo de Padrão', ok: !!watch('tipo_padrao') },
    { label: 'Corrente do Padrão', ok: !!watch('corrente_padrao') },
    { label: 'Fases do Disjuntor', ok: !!watch('fases_disjuntor') },
    { label: 'Serviço Adicional', ok: !!watch('servico_adicional') },
    { label: 'Foto Fachada', ok: arquivos.some((a) => a.categoria === 'foto_fachada') },
    { label: 'Print Marcação do Telhado', ok: arquivos.some((a) => a.categoria === 'print_marcacao_telhado') },
    { label: 'Fotos do Padrão', ok: isLigacaoNova || arquivos.some((a) => a.categoria === 'fotos_padrao') },
    ...(needsProjetoSeparado ? [
      { label: 'Foto do Telhado', ok: arquivos.some((a) => a.categoria === 'foto_telhado') },
      { label: 'Foto Estrutura Abaixo Telhado', ok: arquivos.some((a) => a.categoria === 'foto_estrutura_abaixo_telhado') },
      { label: 'Foto Local do Inversor', ok: arquivos.some((a) => a.categoria === 'foto_local_inversor') },
    ] : []),
    // Itens extras de homologação prévia (só aparecem quando Sim)
    ...(homologacaoPrevia ? [
      { label: 'Proposta vinculada', ok: !!watch('idProposta') },
      { label: 'Nome do Cliente', ok: !!watch('nomeResponsavel') },
      { label: 'CPF/CNPJ do Cliente', ok: !!(watch('cpfResponsavel') || watch('cnpjEmpresa')) },
      { label: 'Endereço de Faturamento', ok: !!(watch('cepFaturamento') && watch('logradouroFaturamento') && watch('numeroFaturamento')) },
      { label: 'Modalidade de Compensação', ok: !!watch('modalidadeCompensacao') },
      { label: 'Documento de Identidade', ok: arquivos.some((a) => a.categoria === 'doc_identidade_cliente') },
      { label: 'Comprovante de Endereço', ok: arquivos.some((a) => a.categoria === 'comprovante_endereco_cliente') },
      ...(watch('tipoCliente') === 'Pessoa Jurídica' ? [
        { label: 'Cartão CNPJ', ok: arquivos.some((a) => a.categoria === 'cartao_cnpj') },
        { label: 'Contrato Social', ok: arquivos.some((a) => a.categoria === 'contrato_social') },
      ] : []),
      ...(watch('titularIgualCliente') === false ? [
        { label: 'Documento do Titular', ok: arquivos.some((a) => a.categoria === 'doc_identidade_titular') },
      ] : []),
    ] : []),
  ];
  const completedCount = checklistItems.filter((i) => i.ok).length;

  if (checkingExistentes) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spin size="large" tip="Verificando homologações..." />
      </div>
    );
  }

  return (
    <FormProvider {...form}>
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* ── Header ── */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight text-black">Homologação</h1>
              {homologacaoId && <span className="text-xs text-neutral-300 font-mono">#{homologacaoId}</span>}
            </div>
            {lastSaved && <span className="text-[11px] text-neutral-300">Salvo às {lastSaved}</span>}
          </div>
          {/* Stepper */}
          <div className="flex flex-wrap justify-center gap-x-2 gap-y-3 sm:gap-x-4">
            {activeSteps.map((s, i) => {
              const t = s.title;
              const status = stepStatus(i);
              const isActive = i === step;
              const isDone = status === 'finish';
              const hasError = status === 'error';
              return (
                <button key={i} onClick={() => { autoSave(); setStep(i); }}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium transition-all border ${
                    isActive
                      ? 'bg-black text-white border-black shadow-sm'
                      : isDone
                        ? 'bg-neutral-100 text-neutral-700 border-neutral-200'
                        : hasError
                          ? 'bg-red-50 text-red-500 border-red-200'
                          : 'bg-white text-neutral-400 border-neutral-200 hover:text-neutral-600 hover:border-neutral-300'
                  }`}>
                  <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${
                    isActive ? 'bg-white text-black' :
                    isDone ? 'bg-neutral-700 text-white' :
                    hasError ? 'bg-red-100 text-red-500' :
                    'bg-neutral-100 text-neutral-400'
                  }`}>
                    {isDone ? <CheckCircleOutlined style={{ fontSize: 10 }} /> : i + 1}
                  </span>
                  <span className="hidden sm:inline">{t}</span>
                  <span className="sm:hidden">{t.length > 10 ? t.split(' ')[0] : t}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── Card dados do cliente (quando vêm da URL) ── */}
        {fromUrl && (
          <div className="border border-neutral-200 rounded-lg p-4 mb-8 flex flex-wrap gap-6 items-center">
            <div>
              <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-medium">Cliente</span>
              <p className="text-sm font-semibold text-black">{urlNome || '—'}</p>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-medium">Tipo</span>
              <p className="text-sm font-semibold text-black">{urlTipoPessoa === 'PJ' ? 'Pessoa Jurídica' : urlTipoPessoa === 'PF' ? 'Pessoa Física' : '—'}</p>
            </div>
            {urlCpfCnpj && (
              <div>
                <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-medium">{urlTipoPessoa === 'PJ' ? 'CNPJ' : 'CPF'}</span>
                <p className="text-sm font-semibold text-black">{urlCpfCnpj}</p>
              </div>
            )}
            {urlPotencia && (
              <div>
                <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-medium">Potência</span>
                <p className="text-sm font-semibold text-black">{urlPotencia} kWp</p>
              </div>
            )}
            {urlEstrutura && (
              <div>
                <span className="text-[10px] uppercase tracking-widest text-neutral-400 font-medium">Estrutura</span>
                <p className="text-sm font-semibold text-black">{TIPO_ESTRUTURA_INSTALACAO_OPTIONS.find(o => o.value === urlEstrutura)?.label ?? urlEstrutura}</p>
              </div>
            )}
          </div>
        )}

        {/* ═══ STEP: Tipo (Homologação Prévia?) ═══ */}
        {currentStepKey === 'tipo' && (
          <div className="border border-neutral-200 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold text-black pb-4 mb-6 border-b border-neutral-100">Tipo de Homologação</h2>
            <p className="text-sm text-neutral-500 mb-6">
              Homologação prévia inclui também os dados do cliente, endereço e projeto completo — que serão reaproveitados automaticamente no momento da criação do contrato. Marque "Sim" apenas se ainda não existe contrato para este cliente.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-neutral-600 mb-3">É homologação prévia? <span className="text-red-500">*</span></label>
              <Controller
                name="homologacao_previa"
                control={form.control}
                render={({ field }) => (
                  <div className="flex gap-3">
                    <button type="button" onClick={() => field.onChange(true)}
                      className={`px-6 py-2.5 rounded-md text-sm font-medium border transition-all ${
                        field.value === true ? 'bg-black text-white border-black' : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400'
                      }`}>
                      Sim
                    </button>
                    <button type="button" onClick={() => field.onChange(false)}
                      className={`px-6 py-2.5 rounded-md text-sm font-medium border transition-all ${
                        field.value === false ? 'bg-black text-white border-black' : 'bg-white text-neutral-600 border-neutral-200 hover:border-neutral-400'
                      }`}>
                      Não
                    </button>
                  </div>
                )}
              />
            </div>
            {homologacaoPrevia && (
              <Alert
                type="info"
                showIcon
                className="mt-4"
                message="Modo prévia ativo"
                description="Os próximos steps pedirão dados de cliente, titular e endereço. Esses dados serão gravados na homologação e puxados automaticamente na criação do contrato deste cliente."
              />
            )}
          </div>
        )}

        {/* ═══ STEP: Cliente (só com homologação prévia) ═══ */}
        {currentStepKey === 'cliente' && (
          <div className="border border-neutral-200 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold text-black pb-4 mb-6 border-b border-neutral-100">Dados do Cliente</h2>
            <Row gutter={16}>
              <Col xs={24} sm={8}><SelectField name="tipoCliente" label="Tipo de Cliente" options={TIPO_CLIENTE_OPTIONS} required /></Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}><FormField name="nomeResponsavel" label={tipoClienteWiz === 'Pessoa Jurídica' ? 'Nome do Responsável' : 'Nome Completo'} required /></Col>
              <Col xs={24} sm={12}><MaskedInput name="cpfResponsavel" label="CPF do Responsável" mask={CPF_MASK} required /></Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}><FormField name="email" label="E-mail" type="email" required /></Col>
              <Col xs={24} sm={12}><MaskedInput name="telefone" label="Telefone" mask={PHONE_MASK} required /></Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={12}><SelectField name="estadoCivil" label="Estado Civil" options={ESTADO_CIVIL_OPTIONS} required /></Col>
              <Col xs={24} sm={12}><FormField name="profissao" label="Profissão" required /></Col>
            </Row>
            {tipoClienteWiz === 'Pessoa Jurídica' && (
              <>
                <div className="mt-6 pt-4 border-t border-neutral-100">
                  <h3 className="text-sm font-semibold text-neutral-700 mb-4">Dados da Pessoa Jurídica</h3>
                </div>
                <Row gutter={16}>
                  <Col xs={24} sm={12}><FormField name="razaoSocial" label="Razão Social" required /></Col>
                  <Col xs={24} sm={12}><MaskedInput name="cnpjEmpresa" label="CNPJ" mask={CNPJ_MASK} required /></Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={12}><FormField name="finalidadeEmpresa" label="Finalidade da Empresa" /></Col>
                  <Col xs={24} sm={12}><FormField name="cargoResponsavel" label="Cargo do Responsável" /></Col>
                </Row>
              </>
            )}

            <div className="mt-6 pt-4 border-t border-neutral-100">
              <h3 className="text-sm font-semibold text-neutral-700 mb-4">Documentos do Cliente</h3>
            </div>
            <Row gutter={16}>
              <Col xs={24} sm={12}><UploadField label="Documento de Identidade (RG/CNH)" categoria="doc_identidade_cliente" required {...uploadProps} /></Col>
              <Col xs={24} sm={12}><UploadField label="Comprovante de Endereço" categoria="comprovante_endereco_cliente" required {...uploadProps} /></Col>
            </Row>
            {tipoClienteWiz === 'Pessoa Jurídica' && (
              <Row gutter={16}>
                <Col xs={24} sm={12}><UploadField label="Cartão CNPJ" categoria="cartao_cnpj" required {...uploadProps} /></Col>
                <Col xs={24} sm={12}><UploadField label="Contrato Social ou Última Alteração" categoria="contrato_social" required {...uploadProps} /></Col>
              </Row>
            )}
          </div>
        )}

        {/* ═══ STEP: Titular (só com homologação prévia) ═══ */}
        {currentStepKey === 'titular' && (
          <div className="border border-neutral-200 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold text-black pb-4 mb-6 border-b border-neutral-100">Titular da Conta de Energia</h2>
            <div className="mb-5">
              <label className="block text-sm font-medium text-neutral-600 mb-2">O titular da conta é o mesmo cliente?</label>
              <Controller
                name="titularIgualCliente"
                control={form.control}
                render={({ field }) => (
                  <Switch checked={field.value !== false} onChange={field.onChange} />
                )}
              />
              <span className="ml-3 text-sm text-neutral-400">{titularIgualCliente !== false ? 'Sim, é o mesmo cliente' : 'Não, titular diferente'}</span>
            </div>
            {titularIgualCliente === false && (
              <>
                <Row gutter={16}>
                  <Col xs={24} sm={8}><SelectField name="titularTipo" label="Tipo do Titular" options={TIPO_CLIENTE_OPTIONS} required /></Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={12}><FormField name="titularNome" label="Nome do Titular" required /></Col>
                  {titularTipoWiz === 'Pessoa Jurídica' ? (
                    <Col xs={24} sm={12}><MaskedInput name="titularCnpj" label="CNPJ do Titular" mask={CNPJ_MASK} required /></Col>
                  ) : (
                    <Col xs={24} sm={12}><MaskedInput name="titularCpf" label="CPF do Titular" mask={CPF_MASK} required /></Col>
                  )}
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={12}><FormField name="titularEmail" label="E-mail do Titular" type="email" required /></Col>
                  <Col xs={24} sm={12}><MaskedInput name="titularTelefone" label="Telefone do Titular" mask={PHONE_MASK} required /></Col>
                </Row>
                {titularTipoWiz === 'Pessoa Jurídica' ? (
                  <Row gutter={16}>
                    <Col xs={24} sm={12}><FormField name="titularRazaoSocial" label="Razão Social" required /></Col>
                    <Col xs={24} sm={12}><FormField name="titularCargoResponsavel" label="Cargo do Responsável" /></Col>
                    <Col xs={24}><FormField name="titularFinalidadeEmpresa" label="Finalidade da Empresa" /></Col>
                  </Row>
                ) : (
                  <Row gutter={16}>
                    <Col xs={24} sm={12}><SelectField name="titularEstadoCivil" label="Estado Civil" options={ESTADO_CIVIL_OPTIONS} /></Col>
                    <Col xs={24} sm={12}><FormField name="titularProfissao" label="Profissão" /></Col>
                  </Row>
                )}

                <div className="mt-6 pt-4 border-t border-neutral-100">
                  <h3 className="text-sm font-semibold text-neutral-700 mb-4">Documentos do Titular</h3>
                </div>
                <Row gutter={16}>
                  <Col xs={24} sm={12}><UploadField label="Documento de Identidade do Titular" categoria="doc_identidade_titular" required {...uploadProps} /></Col>
                  {titularTipoWiz === 'Pessoa Jurídica' && (
                    <Col xs={24} sm={12}><UploadField label="Contrato Social do Titular" categoria="contrato_social_titular" required {...uploadProps} /></Col>
                  )}
                </Row>
              </>
            )}
          </div>
        )}

        {/* ═══ STEP: Endereço (só com homologação prévia) ═══ */}
        {currentStepKey === 'endereco' && (
          <div className="border border-neutral-200 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold text-black pb-4 mb-6 border-b border-neutral-100">Endereço de Faturamento</h2>
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                <div onBlur={() => handleCepBlur('Faturamento')}>
                  <MaskedInput name="cepFaturamento" label="CEP" mask={CEP_MASK} required />
                </div>
              </Col>
              <Col xs={24} sm={16}><FormField name="logradouroFaturamento" label="Logradouro" required /></Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={6}><FormField name="numeroFaturamento" label="Número" required /></Col>
              <Col xs={24} sm={8}><FormField name="complementoFaturamento" label="Complemento" /></Col>
              <Col xs={24} sm={10}><FormField name="bairroFaturamento" label="Bairro" required /></Col>
            </Row>
            <Row gutter={16}>
              <Col xs={24} sm={16}><FormField name="cidadeFaturamento" label="Cidade" required /></Col>
              <Col xs={24} sm={8}><SelectField name="ufFaturamento" label="UF" options={UF_OPTIONS} required /></Col>
            </Row>

            <div className="mt-8 pt-6 border-t border-neutral-100">
              <h3 className="text-md font-semibold text-black mb-3">Endereço de Instalação</h3>
              <div className="mb-5">
                <label className="block text-sm font-medium text-neutral-600 mb-2">Endereço de instalação é igual ao de faturamento?</label>
                <Controller
                  name="instalacaoIgualContratual"
                  control={form.control}
                  render={({ field }) => (
                    <Switch checked={field.value !== false} onChange={field.onChange} />
                  )}
                />
                <span className="ml-3 text-sm text-neutral-400">{instalacaoIgualContratual !== false ? 'Sim, mesmo endereço' : 'Não, endereço diferente'}</span>
              </div>
              {instalacaoIgualContratual === false && (
                <>
                  <Row gutter={16}>
                    <Col xs={24} sm={8}>
                      <div onBlur={() => handleCepBlur('Instalacao')}>
                        <MaskedInput name="cepInstalacao" label="CEP" mask={CEP_MASK} required />
                      </div>
                    </Col>
                    <Col xs={24} sm={16}><FormField name="logradouroInstalacao" label="Logradouro" required /></Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} sm={6}><FormField name="numeroInstalacao" label="Número" required /></Col>
                    <Col xs={24} sm={8}><FormField name="complementoInstalacao" label="Complemento" /></Col>
                    <Col xs={24} sm={10}><FormField name="bairroInstalacao" label="Bairro" required /></Col>
                  </Row>
                  <Row gutter={16}>
                    <Col xs={24} sm={16}><FormField name="cidadeInstalacao" label="Cidade" required /></Col>
                    <Col xs={24} sm={8}><SelectField name="ufInstalacao" label="UF" options={UF_OPTIONS} required /></Col>
                  </Row>
                </>
              )}

              {/* Mapa + Street View do Local de Instalação */}
              <div className="mt-6 pt-5 border-t border-neutral-100">
                <h4 className="text-sm font-semibold text-neutral-700 mb-3">Local da Instalação no mapa</h4>
                <UltimatePicker
                  value={(form.watch('coordenada_geografica') as string) || ''}
                  onChange={(v: string) => form.setValue('coordenada_geografica' as any, v)}
                  linkValue={(form.watch('url_local_instalacao') as string) || ''}
                  onLinkChange={(v: string) => form.setValue('url_local_instalacao' as any, v)}
                  streetViewValue={(form.watch('link_street_view') as string) || ''}
                  onStreetViewChange={(v: string) => form.setValue('link_street_view' as any, v)}
                  thumbnailValue={(form.watch('thumbnail_street_view') as string) || ''}
                  onThumbnailChange={(v: string) => form.setValue('thumbnail_street_view' as any, v)}
                />
              </div>
            </div>
          </div>
        )}

        {/* ═══ STEP 0: Projeto ═══ */}
        {currentStepKey === 'projeto' && (
          <div className="border border-neutral-200 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold text-black pb-4 mb-6 border-b border-neutral-100">Informações Básicas do Projeto</h2>
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <div className="mb-5">
                  <label className="block text-sm font-medium text-neutral-600 mb-1.5">Ampliação de Usina</label>
                  <Controller name="ampliacao_usina" control={form.control}
                    render={({ field }) => <Switch checked={field.value as boolean} onChange={field.onChange} />} />
                </div>
              </Col>
            </Row>
            {!!ampliacao && (
              <>
                <Row gutter={16}>
                  <Col xs={24} sm={12}><FormField name="potencia_existente_kw" label="Potência Existente (kW)" type="number" /></Col>
                  <Col xs={24} sm={12}><FormField name="descricao_usina_existente" label="Descrição dos Materiais da Usina Existente" textarea /></Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24}><UploadField label="Fotos dos Materiais da Usina Existente" categoria="foto_material_usina_existente" {...uploadProps} /></Col>
                </Row>
              </>
            )}
            <Row gutter={16}>
              <Col xs={24} sm={6}>
                <FormField name="potencia_adquirida_kwp" label="Potência Adquirida NOVA (kWp)" type="number" required />
              </Col>
              <Col xs={24} sm={6}>
                <FormField name="numero_modulos" label="Número de Módulos" type="number" disabled={homologacaoPrevia} />
              </Col>
              <Col xs={24} sm={6}><SelectField name="concessionaria" label="Concessionária" options={concOptions} required /></Col>
              <Col xs={24} sm={6}>
                <SelectField name="tipo_estrutura_instalacao" label="Tipo de Estrutura" options={TIPO_ESTRUTURA_INSTALACAO_OPTIONS} disabled={homologacaoPrevia} />
              </Col>
            </Row>
            {needsProjetoSeparado && (
              <Alert
                type="warning"
                icon={<WarningOutlined />}
                showIcon
                className="mb-4"
                message="Formulário de Projeto Separado Necessário"
                description={`Projetos acima de 20 módulos ou com estrutura Solo/Laje requerem o preenchimento de um formulário de projeto separado além desta homologação.${numeroModulos ? ` Este projeto possui ${numeroModulos} módulo${Number(numeroModulos) !== 1 ? 's' : ''}.` : ''}`}
              />
            )}
            <Row gutter={16}>
              <Col xs={24} sm={12}><SelectField name="tipo_localizacao" label="Local de Instalação" options={TIPO_LOCALIZACAO_OPTIONS} required /></Col>
              {isRural && (
                <>
                  <Col xs={24} sm={12}><FormField name="coordenadas_transformador" label="Coordenadas do Transformador" /></Col>
                  <Col xs={24}><UploadField label="Print do Local do Transformador" categoria="print_transformador" {...uploadProps} /></Col>
                  <Col xs={24} sm={12}><UploadField label="CAR (Cadastro Ambiental Rural)" categoria="doc_car" required {...uploadProps} /></Col>
                  <Col xs={24} sm={12}><UploadField label="Documento do Imóvel" categoria="doc_imovel" required {...uploadProps} /></Col>
                </>
              )}
            </Row>
            {!homologacaoPrevia && effectiveIdCliente && (
              <div className="mt-6 pt-5 border-t border-neutral-100 flex items-center justify-between gap-3">
                <span className="text-xs text-neutral-500">
                  {loadingProposta
                    ? 'Buscando proposta no Calc...'
                    : watch('idProposta')
                      ? `Materiais da proposta nº ${watch('numeroProposta') || watch('idProposta')}`
                      : 'Sem proposta vinculada (nenhuma válida no Calc)'}
                </span>
                <button
                  type="button"
                  onClick={() => setPropostaRefresh((k) => k + 1)}
                  disabled={loadingProposta}
                  className="text-xs px-3 py-1 border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50"
                >
                  {loadingProposta ? 'Carregando…' : 'Recarregar proposta'}
                </button>
              </div>
            )}
            {homologacaoPrevia && (
              <div className="mt-6 pt-5 border-t border-neutral-100">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-4 rounded-full bg-black" />
                    <span className="text-xs font-semibold text-black tracking-tight">Dados para o Contrato</span>
                    <span className={`text-[10px] ${effectiveIdCliente && !loadingProposta && !watch('idProposta') ? 'text-red-600 font-medium' : 'text-neutral-400'}`}>
                      {effectiveIdCliente
                        ? (loadingProposta
                            ? 'Buscando proposta no Calc...'
                            : watch('idProposta')
                              ? `Proposta ${watch('numeroProposta') || watch('idProposta') || ''} vinculada`
                              : 'Nenhuma proposta válida — gere uma nova no Calc')
                        : 'Puxado automaticamente no contrato'}
                    </span>
                  </div>
                  {effectiveIdCliente && (
                    <button
                      type="button"
                      onClick={() => setPropostaRefresh((k) => k + 1)}
                      disabled={loadingProposta}
                      className="text-xs px-3 py-1 border border-neutral-300 rounded-md hover:bg-neutral-50 disabled:opacity-50"
                    >
                      {loadingProposta ? 'Carregando…' : 'Recarregar proposta'}
                    </button>
                  )}
                </div>
                {effectiveIdCliente && !loadingProposta && !watch('idProposta') && (
                  <Alert
                    type="error"
                    showIcon
                    className="mb-4"
                    message="Proposta obrigatória para finalizar"
                    description="Nenhuma proposta válida (não vencida) foi encontrada para este cliente no Calc. Gere uma proposta nova no Calc e clique em “Recarregar proposta”. Sem proposta vinculada, a homologação prévia não pode ser finalizada."
                  />
                )}
                {propostas.length > 1 && (
                  <div className="mb-4 flex items-center gap-2">
                    <label className="text-xs text-neutral-500">Proposta:</label>
                    <select
                      className="text-xs border border-neutral-300 rounded-md px-2 py-1 bg-white"
                      value={propostaSelecionada?.IdPedidoBanco || ''}
                      onChange={(e) => {
                        const p = propostas.find((x) => x.IdPedidoBanco === e.target.value);
                        if (p) {
                          setPropostaSelecionada(p);
                          if (!bloqueadoPorEstoque(p)) aplicarProposta(p);
                        }
                      }}
                    >
                      {propostas.map((p) => (
                        <option key={p.IdPedidoBanco} value={p.IdPedidoBanco}>
                          {p.NumeroProposta || p.IdPedidoBanco} — {p.Data ? new Date(p.Data).toLocaleDateString('pt-BR') : ''}
                          {p.estoqueIndisponivel && p.estoqueIndisponivel.length ? ' (sem estoque)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {propostaSelecionada?.estoqueIndisponivel && propostaSelecionada.estoqueIndisponivel.length > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    className="mb-4"
                    message="Itens da proposta sem estoque disponível"
                    description={
                      <>
                        <ul className="list-disc pl-5 text-xs">
                          {propostaSelecionada.estoqueIndisponivel.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                        {EMPRESAS_SEGUEM_SEM_ESTOQUE.includes(String(propostaSelecionada.Empresa || '').trim().toUpperCase()) && (
                          <p className="text-xs mt-2 font-medium">
                            A proposta pode prosseguir mesmo sem estoque, mas é necessário comunicar os setores
                            responsáveis (compras/estoque) sobre a falta antes de seguir com a homologação.
                          </p>
                        )}
                      </>
                    }
                  />
                )}
                <Row gutter={16}>
                  <Col xs={24} sm={8}><SelectField name="modalidadeCompensacao" label="Modalidade de Compensação" options={MODALIDADE_COMPENSACAO_OPTIONS} required /></Col>
                  <Col xs={24} sm={8}><SelectField name="localInstalacao" label="Local de Instalação (Urbano/Rural)" options={LOCAL_INSTALACAO_OPTIONS} /></Col>
                  <Col xs={24} sm={8}><SelectField name="tipoEstrutura" label="Tipo de Estrutura (contrato)" options={TIPO_ESTRUTURA_WIZ_OPTIONS} /></Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={8}><FormField name="valorProposta" label="Valor da Proposta (R$)" type="number" disabled /></Col>
                  <Col xs={24} sm={8}><FormField name="modeloModulo" label="Modelo do Módulo" disabled /></Col>
                  <Col xs={24} sm={8}><FormField name="marcaInversores" label="Marca dos Inversores" disabled /></Col>
                </Row>
                {inversoresProposta.length > 1 ? (
                <div className="mb-5">
                  <label className="block text-sm font-medium text-neutral-600 mb-1.5">Inversores da Proposta</label>
                  <ul className="text-sm text-black space-y-1">
                    {inversoresProposta.map((inv) => (
                      <li key={inv.modelo}><span className="font-semibold">{inv.quantidade}x</span> {inv.modelo}</li>
                    ))}
                  </ul>
                  <div className="mt-3 max-w-xs"><FormField name="qtdInversores" label="Qtd. Total de Inversores" type="number" disabled /></div>
                </div>
                ) : (
                <Row gutter={16}>
                  <Col xs={24} sm={8}><FormField name="modeloInversor1" label="Modelo do Inversor" disabled /></Col>
                  <Col xs={24} sm={8}><FormField name="qtdInversor1" label="Qtd. do Modelo Inversor" type="number"
                    onValueChange={(v) => {
                      const qty = Number(v) || 0;
                      form.setValue('qtdInversores', qty);
                      const modelo = String(form.getValues('modeloInversor1') || '');
                      if (modelo) form.setValue('inversores' as any, [{ modelo, quantidade: qty }] as any);
                      const perUnit = extrairPotenciaUnitariaInversorKW(String(form.getValues('modeloInversor1') || ''));
                      if (perUnit > 0) form.setValue('potenciaTotalInversores', Math.round(perUnit * qty * 100) / 100);
                    }} /></Col>
                  <Col xs={24} sm={8}><FormField name="qtdInversores" label="Qtd. Total de Inversores" type="number" /></Col>
                </Row>
                )}
                <Row gutter={16}>
                  <Col xs={24} sm={12}><FormField name="potenciaTotalInversores" label="Potência Total dos Inversores (kW)" type="number" /></Col>
                  <Col xs={24} sm={12}>
                    <div className="mb-5">
                      <label className="block text-sm font-medium text-neutral-600 mb-1.5">Contrato com Seguro</label>
                      <Controller name="contratoComSeguro" control={form.control}
                        render={({ field }) => <Switch checked={field.value as boolean} onChange={field.onChange} />} />
                    </div>
                  </Col>
                </Row>
              </div>
            )}
          </div>
        )}

        {/* ═══ STEP 1: Padrão Atual ═══ */}
        {currentStepKey === 'padrao' && (
          <div className="border border-neutral-200 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold text-black pb-4 mb-6 border-b border-neutral-100">Informações Sobre o Padrão Atual</h2>

            {/* Tipo e Estrutura do Padrão */}
            <Row gutter={16}>
              <Col xs={24} sm={8}><SelectField name="tipo_padrao" label="Tipo de Padrão" options={TIPO_PADRAO_OPTIONS} required /></Col>
              <Col xs={24} sm={8}><SelectField name="tipo_estrutura_padrao" label="Estrutura do Padrão" options={TIPO_ESTRUTURA_PADRAO_OPTIONS} /></Col>
            </Row>

            {/* Se Coletivo */}
            {isColetivo && (
              <>
                <Row gutter={16}>
                  <Col xs={24} sm={8}><SelectField name="tem_disjuntor_geral" label="Tem disjuntor geral?" options={SIM_NAO_OPTIONS} /></Col>
                  {temDisjuntorGeral === 'Sim' && (
                    <Col xs={24} sm={8}><FormField name="corrente_disjuntor_geral" label="Corrente do disjuntor geral" placeholder="Ex: 100A" /></Col>
                  )}
                </Row>
                <Row gutter={16}><Col xs={24}>
                  <UploadField label="Imagem com a relação de todas as caixas, medidores e disjuntores" categoria="foto_relacao_caixas" required {...uploadProps} />
                </Col></Row>
                {/* Doc imóvel sempre no coletivo (não rural) */}
                {needsDocImovelColetivo && (
                  <Row gutter={16}><Col xs={24} sm={12}>
                    <UploadField label="Documento do Imóvel" categoria="doc_imovel" required {...uploadProps} />
                  </Col></Row>
                )}
              </>
            )}

            {/* Tipo de Titular */}
            {!urlTipoPessoa ? (
              <Row gutter={16}>
                <Col xs={24} sm={8}><SelectField name="tipo_titular" label="Tipo de Titular" options={TIPO_TITULAR_OPTIONS} /></Col>
              </Row>
            ) : null}
            {isPJ && (
              <>
                <Row gutter={16}>
                  <Col xs={24} sm={12}><UploadField label="Cartão CNPJ" categoria="cartao_cnpj" required {...uploadProps} /></Col>
                  <Col xs={24} sm={12}><UploadField label="Contrato Social ou Última Alteração Contratual" categoria="contrato_social" required {...uploadProps} /></Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24}><UploadField label="Documentos Adicionais" categoria="documentos_adicionais" {...uploadProps} /></Col>
                </Row>
              </>
            )}

            {/* Todas contas mesmo titular */}
            <Row gutter={16}>
              <Col xs={24} sm={8}><SelectField name="todas_contas_mesmo_titular" label="Todas as contas no mesmo nome do titular?" options={SIM_NAO_OPTIONS} required /></Col>
            </Row>
            {todasContasTitular === 'Nao' && (
              <Row gutter={16}>
                <Col xs={24} sm={12}><UploadField label="Cessão de Telhado" categoria="cessao_telhado" required {...uploadProps} /></Col>
                <Col xs={24} sm={12}><UploadField label="Documento do Titular (diferente)" categoria="doc_titular" required {...uploadProps} /></Col>
              </Row>
            )}

            {/* Fases do disjuntor */}
            <Row gutter={16}>
              <Col xs={24} sm={12}><SelectField name="fases_disjuntor" label="Quantidade de Fases do Disjuntor Atual" options={FASES_OPTIONS} required /></Col>
            </Row>

            {/* Alteração de carga */}
            <Row gutter={16}>
              <Col xs={24} sm={8}><SelectField name="tem_alteracao_carga" label="Haverá alteração de carga?" options={SIM_NAO_OPTIONS} /></Col>
            </Row>
            {isAlteracaoCarga && (
              <>
                <Row gutter={16}>
                  <Col xs={24} sm={8}><SelectField name="tipo_alteracao_carga" label="Tipo de Alteração" options={TIPO_ALTERACAO_CARGA_OPTIONS} required /></Col>
                </Row>
                {isAlteracaoCliente && (
                  <Alert
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                    className="mb-4"
                    message="Atenção: Homologação Pausada"
                    description="A homologação ficará pausada até a conclusão do padrão pelo cliente (padronista)."
                  />
                )}
                <Row gutter={16}><Col xs={24}><FormField name="descricao_alteracao" label="Descrição da Alteração" textarea required
                  placeholder={`Descreva conforme exemplo:\n1 x 40A para 2 x 63A\n2 x 63A para 3 x 100A\n(1=mono, 2=bi, 3=tri)`} /></Col></Row>
                {/* Doc imóvel: individual + alteração + urbano (coletivo já tem acima, rural no step 0) */}
                {needsDocImovelAlteracao && (
                  <Row gutter={16}><Col xs={24} sm={12}>
                    <UploadField label="Documento do Imóvel" categoria="doc_imovel" required {...uploadProps} />
                  </Col></Row>
                )}
                {needsIptu && (
                  <Row gutter={16}><Col xs={24} sm={12}>
                    <UploadField label="IPTU ou Certidão de Número" categoria="iptu_certidao" required {...uploadProps} />
                  </Col></Row>
                )}
                <Row gutter={16}>
                  <Col xs={24} sm={12}><FormField name="coord_padrao_novo" label="Coordenadas do *NOVO* local do padrão (UTM)" textarea /></Col>
                  <Col xs={24} sm={12}><UploadField label="Print do local *NOVO* do padrão" categoria="print_localizacao" {...uploadProps} /></Col>
                </Row>
              </>
            )}

            {/* Coletivo: alteração para padrão coletivo */}
            {isColetivo && (
              <>
                <Row gutter={16}>
                  <Col xs={24} sm={8}><SelectField name="alteracao_coletivo" label="Terá alteração para o padrão coletivo?" options={SIM_NAO_OPTIONS} /></Col>
                </Row>
                {alteracaoColetivo === 'Sim' && (
                  <Row gutter={16}><Col xs={24}><FormField name="descricao_alteracao_coletivo" label="Descrição da alteração do padrão coletivo" textarea /></Col></Row>
                )}
              </>
            )}

            {/* Corrente do padrão */}
            <Row gutter={16}>
              <Col xs={24} sm={8}><FormField name="corrente_padrao" label="Corrente do Padrão onde será instalada a usina" placeholder="Ex: 63A" required /></Col>
            </Row>

            {/* Campos extras para projeto separado (>20 módulos ou Solo/Laje) */}
            {needsProjetoSeparado && (
              <div className="mt-6 border border-neutral-200 rounded-lg p-5 bg-neutral-50/50">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1 h-4 rounded-full bg-black" />
                  <span className="text-xs font-semibold text-black tracking-tight">Projeto Executivo</span>
                  <span className="text-[10px] text-neutral-400">Acima de 20 módulos / Solo / Laje</span>
                </div>
                <Row gutter={16}>
                  <Col xs={24} sm={8}><SelectField name="favor_contra_rede" label="Local do Padrão em Relação à Rede" options={FAVOR_CONTRA_REDE_OPTIONS} /></Col>
                  <Col xs={24} sm={8}><SelectField name="servico_alvenaria" label="Terá Serviço de Alvenaria?" options={SERVICO_ALVENARIA_OPTIONS} /></Col>
                </Row>
              </div>
            )}

            {/* Fotos do padrão — ocultar se Ligação Nova (padrão ainda não existe) */}
            {!isLigacaoNova && (
              <Row gutter={16}>
                <Col xs={24} sm={12}><UploadField label="Anexar Fotos do Padrão" categoria="fotos_padrao" {...uploadProps} /></Col>
                <Col xs={24} sm={12}><UploadField label="Imagens: Ramal, Disjuntor, Medidor, Caixa de Medição" categoria="imagens_diversas_padrao" {...uploadProps} /></Col>
              </Row>
            )}

            {/* Descrição */}
            <Row gutter={16}><Col xs={24}><FormField name="descricao_padrao" label="Descrição Sobre o Padrão" textarea /></Col></Row>
          </div>
        )}

        {/* ═══ STEP 2: Serviços Adicionais ═══ */}
        {currentStepKey === 'servicos' && (
          <div className="border border-neutral-200 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold text-black pb-4 mb-6 border-b border-neutral-100">Serviços Adicionais</h2>
            <Row gutter={16}>
              <Col xs={24} sm={8}>
                {needsProjetoSeparado ? (
                  <SelectField name="servico_adicional" label="Haverá Serviço Adicional?" options={SERVICO_ADICIONAL_DETALHADO_OPTIONS} required />
                ) : (
                  <SelectField name="servico_adicional" label="Haverá Serviço Adicional?" options={SIM_NAO_OPTIONS} required />
                )}
              </Col>
            </Row>
            {(servicoAdicional === 'Sim' || (needsProjetoSeparado && !!servicoAdicional && servicoAdicional !== 'Nao')) && (
              <Row gutter={16}><Col xs={24}>
                <FormField name="descricao_servico_adicional" label="Descrição do Serviço Adicional" textarea required
                  placeholder="Descreva o serviço adicional que será realizado..." />
              </Col></Row>
            )}
            {homologacaoPrevia && (
              <div className="mt-6 pt-5 border-t border-neutral-100">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1 h-4 rounded-full bg-black" />
                  <span className="text-xs font-semibold text-black tracking-tight">Dados para o Contrato</span>
                  <span className="text-[10px] text-neutral-400">Puxado automaticamente no contrato</span>
                </div>
                <Row gutter={16}>
                  <Col xs={24} sm={8}><SelectField name="situacaoPadrao" label="Situação do Padrão" options={SITUACAO_PADRAO_OPTIONS} /></Col>
                  <Col xs={24} sm={8}><FormField name="quantidadePadroes" label="Quantidade de Padrões" type="number" /></Col>
                  <Col xs={24} sm={8}><SelectField name="tipoConexaoPadrao" label="Tipo de Conexão do Padrão" options={TIPO_CONEXAO_PADRAO_OPTIONS} /></Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={8}><SelectField name="correnteDisjuntor" label="Corrente do Disjuntor (contrato)" options={CORRENTE_DISJUNTOR_OPTIONS} /></Col>
                  <Col xs={24} sm={8}><SelectField name="favorOuContraRede" label="A Favor ou Contra a Rede" options={FAVOR_OU_CONTRA_REDE_OPTIONS} /></Col>
                  <Col xs={24} sm={8}><SelectField name="servicoAlvenariaWiz" label="Serviço de Alvenaria (contrato)" options={SERVICO_ALVENARIA_WIZ_OPTIONS} /></Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={12}><FormField name="valorPadrao" label="Valor do Padrão (R$)" type="number" /></Col>
                </Row>
                {situacaoPadraoWiz === 'Alteração de carga' && (
                  <Row gutter={16}>
                    <Col xs={24}><FormField name="coordPadraoNovo" label="Coordenadas do novo padrão (contrato)" textarea /></Col>
                  </Row>
                )}
                <Row gutter={16}>
                  <Col xs={24}><FormField name="aditivos" label="Aditivos do Contrato" textarea placeholder="Descreva aditivos que serão incluídos no contrato..." /></Col>
                </Row>
              </div>
            )}
          </div>
        )}

        {/* ═══ STEP 3: Fotos Técnicas ═══ */}
        {currentStepKey === 'fotos' && (
          <div className="border border-neutral-200 rounded-lg p-6 mb-8">
            <h2 className="text-lg font-semibold text-black pb-4 mb-6 border-b border-neutral-100">Fotos de Documentação Técnica</h2>
            <Row gutter={16}>
              <Col xs={24} sm={12}><UploadField label="Foto da Fachada da Casa" categoria="foto_fachada" required {...uploadProps} /></Col>
              <Col xs={24} sm={12}><UploadField label="Print da Marcação do Telhado" categoria="print_marcacao_telhado" required {...uploadProps} /></Col>
            </Row>
            {needsProjetoSeparado && (
              <div className="mt-2 border border-neutral-200 rounded-lg p-5 bg-neutral-50/50">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-1 h-4 rounded-full bg-black" />
                  <span className="text-xs font-semibold text-black tracking-tight">Projeto Executivo</span>
                  <span className="text-[10px] text-neutral-400">Acima de 20 módulos / Solo / Laje</span>
                </div>
                <Row gutter={16}>
                  <Col xs={24} sm={12}><UploadField label="Foto do Telhado" categoria="foto_telhado" required {...uploadProps} /></Col>
                  <Col xs={24} sm={12}><UploadField label="Foto da Estrutura Abaixo do Telhado" categoria="foto_estrutura_abaixo_telhado" required {...uploadProps} /></Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={12}><UploadField label="Foto do Local do Inversor" categoria="foto_local_inversor" required {...uploadProps} /></Col>
                  <Col xs={24} sm={12}><UploadField label="Foto do Acesso ao Telhado (Opcional)" categoria="foto_acesso_telhado" {...uploadProps} /></Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={12}><UploadField label="Foto do Quadro de Conexão (Opcional)" categoria="foto_quadro_conexao" {...uploadProps} /></Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={24} sm={12}><UploadField label="Foto do Quadro de Passagem (Opcional)" categoria="foto_quadro_passagem" {...uploadProps} /></Col>
                </Row>
              </div>
            )}
            <Row gutter={16}><Col xs={24}><FormField name="observacoes_fotos" label="Observação sobre Fotos" textarea /></Col></Row>
          </div>
        )}

        {/* ═══ STEP 4: Finalizar ═══ */}
        {currentStepKey === 'finalizar' && (
          <>
            {needsProjetoSeparado && (
              <div className="border border-neutral-200 rounded-lg p-6 mb-8 bg-neutral-50/50">
                <div className="flex items-center gap-2 pb-4 mb-6 border-b border-neutral-100">
                  <div className="w-1 h-5 rounded-full bg-black" />
                  <div>
                    <h2 className="text-lg font-semibold text-black leading-tight">Confirmação do Projeto</h2>
                    <span className="text-[10px] text-neutral-400">Projeto Executivo — Acima de 20 módulos / Solo / Laje</span>
                  </div>
                </div>
                <Row gutter={16}>
                  <Col xs={24} sm={8}>
                    <SelectField name="projeto_aprovado" label="Projeto Aprovado" options={SIM_NAO_OPTIONS} />
                  </Col>
                  {watch('projeto_aprovado') === 'Sim' && (
                    <Col xs={24} sm={16}>
                      <FormField name="nome_parecer_aprovado" label="Nome do Parecer Aprovado" />
                    </Col>
                  )}
                </Row>
              </div>
            )}
            <div className="border border-neutral-200 rounded-lg p-6 mb-8">
              <h2 className="text-lg font-semibold text-black pb-4 mb-6 border-b border-neutral-100">Observações Gerais</h2>
              <FormField name="observacoes_finais" label="Observações" textarea />
            </div>
            <div className="border border-neutral-200 rounded-lg p-6 mb-8">
              <div className="flex items-center justify-between pb-4 mb-4 border-b border-neutral-100">
                <h2 className="text-lg font-semibold text-black">Checklist</h2>
                <span className={`text-xs font-mono ${completedCount === checklistItems.length ? 'text-black' : 'text-neutral-400'}`}>
                  {completedCount}/{checklistItems.length}
                </span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                {checklistItems.map((item) => (
                  <div key={item.label} className={`flex items-center gap-2.5 px-3 py-2 rounded-md ${item.ok ? 'bg-neutral-50' : 'bg-white'}`}>
                    {item.ok
                      ? <CheckCircleOutlined className="text-black" />
                      : <ExclamationCircleOutlined className="text-neutral-300" />}
                    <span className={`text-sm ${item.ok ? 'text-black' : 'text-neutral-400'}`}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ── Navegação ── */}
        <div className="flex justify-between mb-12 mt-2">
          <button onClick={handlePrev} disabled={step === 0}
            className="text-sm font-medium text-neutral-500 border border-neutral-200 rounded-md px-5 py-2.5 hover:bg-neutral-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
            Voltar
          </button>
          <div className="flex gap-3">
            {step < activeSteps.length - 1 ? (
              <button onClick={handleNext}
                className="text-sm font-medium text-white bg-black rounded-md px-6 py-2.5 hover:bg-neutral-800 transition-colors">
                Continuar
              </button>
            ) : (
              <button disabled={submitting} onClick={async () => {
                // Regra B: prévia só finaliza com proposta válida (não vencida) vinculada.
                if (homologacaoPrevia && !form.getValues('idProposta')) {
                  toast.error('Proposta vencida ou inexistente. Gere uma proposta nova no Calc e clique em "Recarregar proposta" antes de finalizar.');
                  setStep(activeSteps.findIndex((s) => s.key === 'projeto'));
                  return;
                }
                const ok = await finalizar(form.getValues());
                if (ok && resolvedClienteId) navigate(`/homologacoes/${resolvedClienteId}`);
              }}
                className="text-sm font-medium text-white bg-black rounded-md px-6 py-2.5 hover:bg-neutral-800 transition-colors disabled:opacity-50">
                {submitting ? 'Finalizando...' : 'Finalizar Homologação'}
              </button>
            )}
          </div>
        </div>
      </div>
    </FormProvider>
  );
}
